import {
    ConsentLevel,
    ScriptLoadResult,
    useScriptWithConsent,
} from '@news-mono/web-common'
import React from 'react'
import { LoggerContext } from '../../../diagnostics/LoggerContext'
import { ConsentFallback } from '../../../tracking/ConsentFallback/ConsentFallback'
import { TextLink } from '../../../typography/TextLink/TextLink'
import { useSectionMetaInfo } from '../../../__helpers/use-section-meta'
import { initInsta } from '../helpers/instagram'
import { OEmbedResult } from '../helpers/oembed'

export interface InstagramEmbedProps {
    link: string
}

export const InstagramEmbed: React.FC<InstagramEmbedProps> = ({ link }) => {
    const { FacebookAppId, FacebookClientToken } = useSectionMetaInfo() as any // TODO update
    const logger = React.useContext(LoggerContext)

    const [instagramHtml, setInstagramHtml] = React.useState('')

    const [result, error] = useScriptWithConsent({
        consentMet: {
            src: 'https://platform.instagram.com/en_US/embeds.js',
        },
        consentRequiredLevel: ConsentLevel.All,
    })

    React.useEffect(() => {
        async function getInstagramOembed() {
            try {
                const instaUrl = encodeURIComponent(link)
                /**
                 * Access token generated from appid / clienttoken => {app-id}|{client-token}
                 * https://developers.facebook.com/docs/instagram/oembed/
                 */
                const accessToken = `${FacebookAppId}|${FacebookClientToken}`

                // New Oembed api - omit the script
                const graphRequest = await fetch(
                    `https://graph.facebook.com/v8.0/instagram_oembed?omitscript=true&url=${instaUrl}&access_token=${accessToken}`,
                )

                const instaResp = (await graphRequest.json()) as OEmbedResult

                const html = instaResp.html

                if (html) {
                    setInstagramHtml(html)
                    initInsta()
                }
            } catch (error) {
                logger.debug({ error, link }, 'Error loading instagram post')
            }
        }
        if (result === ScriptLoadResult.Success) {
            getInstagramOembed()
        }
    }, [result, error, link, FacebookAppId, FacebookClientToken, logger])

    if (result === ScriptLoadResult.NotConsented) {
        return <ConsentFallback />
    }

    if (result === ScriptLoadResult.Failed) {
        return (
            <TextLink href={link} openInNewWindow={true}>
                View Instagram Post
            </TextLink>
        )
    }

    if (!instagramHtml || result === ScriptLoadResult.Loading) {
        return (
            <TextLink href={link} openInNewWindow={true}>
                Loading Instagram Post
            </TextLink>
        )
    }

    if (instagramHtml) {
        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: instagramHtml,
                }}
            />
        )
    }

    return null
}

export default InstagramEmbed
